/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Header from "../../Header";
import { theme } from "styles/theme";
import { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import InputField from "uis/InputField";
import Icon from "uis/Icon";
import "react-datepicker/dist/react-datepicker.css";
import Button from "uis/Button";
import ko from "date-fns/locale/ko";
import { useParams } from "react-router-dom";
import { decrypt, encrypt } from "utils/crypto";
import { member, util } from "recoil/recoilState";
import { useRecoilValue } from "recoil";
import { TVisit, TVisitInfo, TVisitor } from "apis/visitors/type";
import RadioGroup from "./radio-button-group";
import { getVisitRecord, saveVisitInfo, updateVisitInfo } from "apis/visitors/record";
import VisitorTable, { isValidEmail, isValidPhoneNumber } from "./visitor-table";
import ExcelUploaderBtn from "./upload-csv";
import { getScheduledVisitPurpose } from "apis/visitors/setting/scheduled-visit/purpose";
import { LoadingModal } from "uis/modal";
import TranslationBtn from "uis/translation-btn";
import { translate } from "./translate";
import PersonalVisitToggle from "./personal-visit-toggle";
import { VisitManagerSection } from "./visit-manager-section";
import TimeSelect from "uis/Select/time_select";
import { CSVAlertModal, FailSendMessageModal, SuccessSendMessage, UpdateAlertModal } from "./modal";
import { TMember } from "types/user.types";
import { getMessengers } from "apis/messangers";
import { getPersonalCalendarConnection } from "apis/personal-calendar";
import { StyledDatePickerWrapper } from "./date-picker-styles";

registerLocale("ko", ko);

const RootContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #f9fbff;
`;

const WhiteContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
  width: 1100px;
  margin: 32px auto;
  border-radius: 15px;
  box-shadow: 0px 4px 26px 0px rgba(69, 76, 83, 0.08);
  position: relative;
`;

const UnderLine = styled.div<{ width?: number }>`
  width: ${(props) => (props.width ? props.width + "px" : "100%")};
  height: 5px;
  background-color: ${theme.neutral100};
  margin: 12px 0 40px 0;
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 24px;
`;
const Label = styled.p`
  color: ${theme.neutral900};
  font-weight: 700;
  font-size: 16px;
`;
const SubLabel = styled.p`
  width: 860px;
  color: ${theme.neutral600};
  font-weight: 400;
  font-size: 14px;
`;

const BlockContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 450px;
  margin-bottom: 40px;
`;

const AddRowBtn = styled.div`
  display: flex;
  width: min-content;
  gap: 4px;
  align-items: center;
  white-space: nowrap;
  margin-top: 16px;
  margin-left: auto;
  cursor: pointer;

  color: ${theme.primary500};
  font-size: 16px;
  font-weight: 600;
  &:hover {
    font-weight: 800;
  }
`;

const PickerBtn = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  padding: 0 18px;
  height: 50px;
  align-items: center;
  background-color: ${theme.neutral50};
  border-radius: 10px;

  &:hover {
    background-color: ${theme.neutral100};
  }
`;

const Annotation = styled.p`
  color: ${theme.neutral400};
  font-size: 16px;
  font-weight: 500;
`;

const HowToUploadCSV = styled.a`
  color: ${theme.neutral400};
  font-size: 14px;
  font-weight: 500;
  margin-left: auto;
  text-decoration-line: underline;
`;

export default function Register() {
  const [visitDate, setVisitDate] = useState<Date>(new Date());
  const [visitInfo, setVisitInfo] = useState<TVisitInfo>({
    language: "KOR",
    isGroupVisit: true,
    visitRecordManagers: [],
    isNotificationSentForVisitors: true,
    isNotificationSentForManagers: true,
  });
  const [visitors, setVisitors] = useState<TVisitor[]>([
    { name: "", guideChannel: "KAKAO", email: null, phoneNumber: null },
  ]);
  const [openCSVModal, setOpenCSVModal] = useState<boolean>(false);
  const [updateAlertModal, setUpdateAlertModal] = useState<boolean>(false);
  const [tempVisitors, setTempVisitors] = useState<TVisitor[]>([
    { name: "", guideChannel: "KAKAO", email: null, phoneNumber: null },
  ]);
  const { id } = useParams();
  const memberInfo = useRecoilValue(member);
  const [defaultManagers, setDefaultManagers] = useState<TMember[]>([]);
  const [managers, setManagers] = useState<TMember[]>([]);
  const { language } = useRecoilValue(util);
  const [loading, setLoading] = useState<boolean>(false);
  const [sendMessage, setSendMessage] = useState<boolean | undefined>(undefined);
  const [sendResult, setSendResult] = useState<TVisitor[]>([]);
  const [visitId, setVisitId] = useState<string>();
  const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] = useState<boolean>(false);

  /**
   * 방문자의 정보를 확인하여 메세지가 안보내진 방문자가 있으면 모달을 띄워줍니다.
   * @param visitors 등록한 방문자 리스트
   * @returns
   */
  const openSendMessageModal = (visitors: TVisitor[]) => {
    const allSendMessage = visitors.every((item) => item.isGuideMessageSent);
    if (allSendMessage) return true;
    else return false;
  };

  /**
   * 방문자 수정 및 등록 후 카카오톡 메세지 전송 여부를 알려주는 모달을 띄워줍니다.
   * @param registerInfo 등록할 방문 정보
   */
  const callRegisterVisitorInfo = async (registerInfo: TVisitInfo) => {
    setLoading(true);

    let res;
    if (id && member) {
      //수정 로직
      res = await updateVisitInfo(registerInfo);
    } else {
      //등록 로직
      res = await saveVisitInfo(registerInfo);
    }

    if (res.success) {
      setSendResult(res.data?.visitors ?? []);
      setSendMessage(openSendMessageModal(res.data?.visitors ?? []));
      setVisitId(res.data?.id ?? "");
    }

    setLoading(false);
  };

  type NotiSetting = {
    visitor: boolean;
    manager: boolean;
  };

  const checkGoogleCalendarConnected = async () => {
    getPersonalCalendarConnection().then((res) => {
      setIsGoogleCalendarConnected(res.success);
    });
  };

  const getRegisterInfo = (noti: NotiSetting): TVisitInfo => {
    return {
      ...visitInfo,
      visitorCount: visitors.length,
      visitors:
        visitInfo.language === "ENG"
          ? visitors.map((item) => {
              return {
                ...item,
                guideChannel: "EMAIL",
                phoneNumber: item.guideChannel === "EMAIL" ? null : item.phoneNumber,
                email: item.guideChannel === "KAKAO" ? null : item.email,
              };
            })
          : visitors.map((item) => {
              return {
                ...item,
                phoneNumber: item.guideChannel === "EMAIL" ? null : item.phoneNumber,
                email: item.guideChannel === "KAKAO" ? null : item.email,
              };
            }),
      visitDatetime: visitDate.toISOString(),
      visitRecordManagers: managers,
      isNotificationSentForVisitors: noti.visitor,
      isNotificationSentForManagers: noti.manager,
    };
  };

  useEffect(() => {
    if (!id && memberInfo) {
      setDefaultManagers([memberInfo]);
    }
  }, [memberInfo]);

  useEffect(() => {
    if (id) {
      const decoded_id = decodeURIComponent(id);

      getVisitRecord(decrypt(decoded_id) || "").then((res) => {
        if (res.success) {
          if (res.data) {
            setVisitInfo({
              ...res.data,
              visitRecordManagers: [res.data.visitRecordManagers[0].manager],
              language: "KOR",
              isGroupVisit: res.data.isGroupVisit,
              isNotificationSentForVisitors: true,
              isNotificationSentForManagers: true,
            });
            setVisitors(res.data?.visitors ?? []);
            setVisitDate(new Date(res.data.visitDatetime ?? ""));
            setDefaultManagers([res.data.visitRecordManagers[0].manager]);
          }
        }
      });
    }
  }, [id]);

  useEffect(() => {
    checkGoogleCalendarConnected();
  }, []);

  return (
    <RootContainer id="root">
      <Header />
      <WhiteContainer>
        <div
          css={css`
            padding: 120px 120px 0 120px;
          `}
        >
          <TranslationBtn
            allowClick
            styles={{
              container: css`
                position: absolute;
                right: 30px;
                top: 30px;
                border-radius: 100px;
                cursor: pointer;

                &:hover {
                  background-color: ${theme.neutral100};
                }
              `,
              text: css`
                font-size: 16px;
              `,
            }}
            iconSize={20}
          />
          <Title>{translate["기본 정보"][language]}</Title>
          <UnderLine width={450} />

          <BlockContainer>
            <Label>{translate["방문 일자"][language]}</Label>
            <StyledDatePickerWrapper>
              <DatePicker
                selected={visitDate}
                onChange={(date) => date && setVisitDate(date)}
                locale={language}
                minDate={new Date()}
                customInput={
                  <PickerBtn className="example-custom-input">
                    <p
                      css={css`
                        font-size: 15px;
                      `}
                    >
                      {visitDate.getFullYear()}.{visitDate.getMonth() + 1}.{visitDate.getDate()}
                    </p>
                    <Icon name="calendar" color={theme.neutral400} width={24} height={24} />
                  </PickerBtn>
                }
              />
            </StyledDatePickerWrapper>
          </BlockContainer>
          <BlockContainer>
            <Label>{translate["방문 시간"][language]}</Label>
            <TimeSelect
              date={visitDate}
              onChange={(hour, minutes) => {
                const new_date = new Date(visitDate);
                new_date.setHours(hour);
                new_date.setMinutes(minutes);
                setVisitDate(new_date);
              }}
            />
          </BlockContainer>
          <BlockContainer>
            <Label>{translate["방문 목적"][language]}</Label>
            <VisitPurposeSelector
              purpose={visitInfo.visitorPurpose}
              onChange={(value) => {
                setVisitInfo((prev) => ({ ...prev, visitorPurpose: value }));
              }}
            />
          </BlockContainer>
          <BlockContainer>
            <Label>
              {translate["방문자 소속"][language]}{" "}
              <span
                css={css`
                  color: ${theme.neutral400};
                `}
              >
                ({translate["선택"][language]})
              </span>
            </Label>
            <InputField
              value={visitInfo.visitorDepartment}
              placeholder={translate["내용을 입력해주세요."][language]}
              styles={{
                input: css`
                  padding: 16px;
                `,
              }}
              onChange={(value) => {
                setVisitInfo((prev) => ({ ...prev, visitorDepartment: value }));
              }}
            />
          </BlockContainer>
          <BlockContainer>
            <Label>{translate["방문자 안내 언어"][language]}</Label>
            <RadioGroup
              onChange={(item) => {
                if (item === "한국어" || item === "Korean") setVisitInfo({ ...visitInfo, language: "KOR" });
                else {
                  setVisitInfo({ ...visitInfo, language: "ENG" });
                  setVisitors((prev) =>
                    [...prev].map((item, index) => {
                      return { ...item, guideChannel: "EMAIL" };
                    })
                  );
                }
              }}
              data={[translate["한국어"][language], translate["영어"][language]]}
              selectedItem={visitInfo.language === "KOR" ? translate["한국어"][language] : translate["영어"][language]}
              styles={css`
                margin-top: 12px;
              `}
            />
          </BlockContainer>
          <BlockContainer>
            <Label>{translate["방문 담당자"][language]}</Label>
            <SubLabel>{translate["방문 담당자 설명"][language]}</SubLabel>
            <VisitManagerSection
              defaultManagers={defaultManagers}
              onChange={(selectedManager) => {
                setManagers(selectedManager);
              }}
            />
          </BlockContainer>
        </div>
        <Title
          css={css`
            margin-top: 60px;
            padding: 0 120px 12px 120px;
          `}
        >
          {translate["방문자 정보"][language]}
        </Title>
        <PersonalVisitToggle
          value={!visitInfo.isGroupVisit}
          onChange={() => setVisitInfo({ ...visitInfo, isGroupVisit: !visitInfo.isGroupVisit })}
        />
        <div
          css={css`
            padding: 0 120px 0 120px;
          `}
        >
          <div
            css={css`
              display: flex;
              gap: 12px;
              align-items: end;
            `}
          >
            {/* TODO: 추후 가이드 링크 연결 예정  */}
            {/* <HowToUploadCSV
              onClick={() =>
                window.open("https://mile.mintlify.app/visitors/visitor-register/register-visitors-with-csv")
              }
            >
              방문자 Excel 업로드 방법 알아보기
            </HowToUploadCSV> */}
            <Button
              onClick={() =>
                window.open(
                  "https://mile-storage.s3.ap-northeast-2.amazonaws.com/%E1%84%87%E1%85%A1%E1%86%BC%E1%84%86%E1%85%AE%E1%86%AB%E1%84%8C%E1%85%A1%2B%E1%84%8B%E1%85%B5%E1%86%AF%E1%84%80%E1%85%AA%E1%86%AF%2B%E1%84%83%E1%85%B3%E1%86%BC%E1%84%85%E1%85%A9%E1%86%A8%2Bexcel%2B%E1%84%92%E1%85%A7%E1%86%BC%E1%84%89%E1%85%B5%E1%86%A8.xlsx"
                )
              }
              title={translate["Excel 형식 다운로드"][language]}
              styles={{
                container: css`
                  background-color: #81cb7f1a;
                  height: max-content;
                  padding: 12px 20px;

                  &:hover {
                    background-color: #82cb7f4c;
                  }
                `,
                text: css`
                  color: #2eb67d;
                `,
              }}
            />
            <ExcelUploaderBtn
              onChange={(data: TVisitor[]) => {
                setOpenCSVModal(true);
                setTempVisitors(data);
              }}
            />
          </div>
          <VisitorTable visitors={visitors} setVisitors={setVisitors} language={visitInfo.language} />
          <AddRowBtn
            onClick={() => {
              setVisitors([...visitors, { name: "", guideChannel: "KAKAO", phoneNumber: null, email: null }]);
            }}
          >
            <Icon name="plus-circle" width={24} height={24} color={theme.primary500} />
            <p>{translate["방문자 추가하기"][language]}</p>
          </AddRowBtn>
        </div>
        <div
          css={css`
            display: flex;
            flex: 1;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 60px;
            padding: 0 120px 120px 120px;
          `}
        >
          <Annotation>Powered by Mile</Annotation>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              gap: 8px;
              color: ${theme.neutral500};
              font-weight: 500;
              text-decoration: underline 1px ${theme.neutral500};
            `}
          >
            {!isGoogleCalendarConnected && (
              <p
                onClick={() => window.open(`/setting/${encodeURIComponent("서비스 연동 관리")}`)}
                css={css`
                  cursor: pointer;
                  &:hover {
                    color: ${theme.neutral600};
                  }
                `}
              >
                구글 캘린더를 연동하고, 간편한 일정 추가∙관리를 경험하세요!
              </p>
            )}
            <Button
              onClick={async () => {
                if (
                  !visitors.every(
                    (item, index) =>
                      item.name &&
                      (item.guideChannel === "EMAIL"
                        ? isValidEmail(`${item.email}`)
                        : isValidPhoneNumber(`${item.phoneNumber}`))
                  )
                ) {
                  alert(translate["방문자 정보를 모두 기입해주세요."][language]);
                  return;
                }
                if (!visitInfo.visitorPurpose) {
                  alert(translate["방문 정보를 모두 기입해주세요."][language]);
                  return;
                }

                if (id) {
                  setUpdateAlertModal(true);
                } else {
                  const registerInfo = getRegisterInfo({ manager: true, visitor: true });
                  callRegisterVisitorInfo(registerInfo);
                }
              }}
              title={translate["등록하기"][language]}
              styles={{
                container: css`
                  width: 200px;
                  padding: 16px 0;
                `,
              }}
            />
          </div>
        </div>
      </WhiteContainer>

      <CSVAlertModal
        modalIsOpen={openCSVModal}
        closeModal={() => setOpenCSVModal(false)}
        onHandleUpload={function (): void {
          setVisitors(tempVisitors);
        }}
      />
      <UpdateAlertModal
        modalIsOpen={updateAlertModal}
        closeModal={() => {
          setUpdateAlertModal(false);
        }}
        onClickButton={function (type: string): void {
          let registerInfo;
          if (type === "all-notification") {
            registerInfo = getRegisterInfo({ manager: true, visitor: true });
          }
          if (memberInfo && id && registerInfo) callRegisterVisitorInfo(registerInfo);
          setUpdateAlertModal(false);
        }}
        onClickOption={function (type: string): void {
          let registerInfo;
          if (type === "visitor") {
            registerInfo = getRegisterInfo({ manager: false, visitor: true });
          }
          if (memberInfo && id && registerInfo) callRegisterVisitorInfo(registerInfo);
          setUpdateAlertModal(false);
        }}
      />
      <LoadingModal modalIsOpen={loading} />
      <SuccessSendMessage
        modalIsOpen={sendMessage === true}
        sendVisitors={sendResult}
        onHandleBtn={() => {
          if (isGoogleCalendarConnected && id) {
            window.location.href = `/visitors/add-personal-calendar/edit/${encodeURIComponent(id)}`;
          }
          if (isGoogleCalendarConnected) {
            window.location.href = `/visitors/add-personal-calendar/create/${encodeURIComponent(encrypt(`${visitId}`))}`;
          } else {
            window.history.back();
          }
        }}
      />
      <FailSendMessageModal
        modalIsOpen={sendMessage === false}
        sendVisitors={sendResult}
        onHandleBtn={() => {
          if (isGoogleCalendarConnected && id) {
            window.location.href = `/visitors/add-personal-calendar/edit/${encodeURIComponent(id)}`;
          }
          if (isGoogleCalendarConnected) {
            window.location.href = `/visitors/add-personal-calendar/create/${encodeURIComponent(encrypt(`${visitId}`))}`;
          } else {
            window.history.back();
          }
        }}
      />
    </RootContainer>
  );
}

const SelectContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  filter: drop-shadow(0px 4px 20px rgba(69, 76, 83, 0.15));

  position: absolute;
  top: 50px;
  width: 100%;
  max-height: 200px;
  overflow: auto;
`;

const SelectItem = styled.p<{ selected: boolean }>`
  margin: 12px;
  padding: 12px;
  border-radius: 8px;
  background-color: ${(props) => (props.selected ? theme.neutral50 : "white")};
  font-weight: ${(props) => (props.selected ? 700 : 500)};
  &:hover {
    background-color: ${theme.neutral50};
  }
`;

const VisitPurposeSelector = ({ purpose, onChange }: { purpose?: string; onChange?: (value: string) => void }) => {
  const [purposes, setPurposes] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const { language } = useRecoilValue(util);

  useEffect(() => {
    getScheduledVisitPurpose().then((res) => {
      if (res.success && res.data) {
        setPurposes(res.data.map((item) => (item.content ? item.content : "")));
      }
    });
  }, []);

  return (
    <div
      onBlur={() => setOpen(false)}
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <InputField
        onFocus={() => {
          setOpen(true);
        }}
        value={purpose}
        placeholder={translate["내용을 입력해주세요."][language]}
        styles={{
          input: css`
            padding: 16px;
          `,
        }}
        onChange={onChange}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setOpen(false);
          } else setOpen(true);
        }}
      />
      {purposes.length > 0 && open && (
        <SelectContainer>
          {purposes
            .filter((item) => item.includes(`${purpose}`) || !purpose)
            .map((item, index) => (
              <SelectItem
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                selected={purpose === item}
                key={index}
                onClick={() => {
                  onChange?.(item);
                  setOpen(false);
                }}
              >
                {item}
              </SelectItem>
            ))}
        </SelectContainer>
      )}
    </div>
  );
};
